import React, { Fragment, useState, useEffect, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import SendIcon from '@mui/icons-material/Send';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import ChipInput from 'material-ui-chip-input';
import { Editor } from '@tinymce/tinymce-react';
import Handlebars from 'handlebars/dist/cjs/handlebars';
import { sendSingleEmail, askGPT, createFile } from '../../../common/api';
import { toast } from 'react-toastify';
import { useDropzone } from 'react-dropzone';
import { storeMedia, createId, extractImages, render, getMimeTypeFromExtension } from '../../../common/utils';
import ScheduleButton from '../ScheduleButton';
import { MdContentCopy } from 'react-icons/md';


const SendEmail = ( { user, settings, accts, team, contacts, contactEmails, opps, loans, globalStatus, loanStatus, close, obj, objType, tmpl, tmplContent, sub, attachment, extraFields, rates } ) => {
    const [ showCc, setShowCc ] = useState( false );
    const [ showBcc, setShowBcc ] = useState( false );
    const [ from, setFrom ] = useState( '' );
    const [ signature, setSignature ] = useState( '' );
    const [ senderList, setSenderList ] = useState( [] );
    const [ sendersLoaded, setSendersLoaded ] = useState( false );
    const [ to, setTo ] = useState( [] );
    const [ cc, setCc ] = useState( [] );
    const [ bcc, setBcc ] = useState( [] );
    const [ subject, setSubject ] = useState( ( sub ) ? sub : '' );
    const [ body, setBody ] = useState( '' );
    const [ attachments, setAttachments ] = useState( [] );
    const [ videoOption, setVideoOption ] = useState( false );
    const [ showVideo, setShowVideo ] = useState( false );
    const [ loaded, setLoaded ] = useState( false );
    const [ loadedTmpl, setLoadedTmpl ] = useState( false );
    const [ showChatGPT, setShowChatGPT ] = useState( false );
    const [ chatRequest, setChatRequest ] = useState( '' );
    const [ tmplUsed, setTmplUsed ] = useState( '' );
    const [ sending, setSending ] = useState( false );
    const [ allTmpls, setAllTmpls ] = useState( {} );
    const [ prompt, setPrompt ] = useState( '' );
    const [ waiting, setWaiting ] = useState( false );
    const [ displayText, setDisplayText ] = useState( '' );
    const [ chatResp, setChatResp ] = useState( '' );
    const [ replace, setReplace ] = useState( false );
    const [ promptType, setPromptType ] = useState( 'freeform' );

    const editorRef = useRef( null );
    const tmplRef = useRef( null );
    tmplRef.current = allTmpls;
    const senderRef = useRef( null );
    senderRef.current = senderList;
    const bbObjRef = useRef( null );

    const formatRecip = useCallback( ( email ) => {
        const cid = contactEmails[email];
        let recip = { email: email };

        if ( cid ) {
            const c = contacts[cid];
            recip['cid'] = cid;

            if ( c.email && c.email.length && c.email.toLowerCase() === email ) {
                recip['name'] = c.firstname + ' ' + c.lastname;
            }
        }

        return recip;
    }, [ contacts, contactEmails ] );

    const insertAiResponse = () => {
        if ( replace ) {
            editorRef.current.selection.setContent( chatResp );
        } else {
            editorRef.current.insertContent( chatResp );
        }
        setReplace( false );
        setChatResp( '' );
        setDisplayText( '' );
        setShowChatGPT( false );
    };

    const insertAiSubject = ( sub ) => {
        setSubject( sub );
        setReplace( false );
        setChatResp( '' );
        setDisplayText( '' );
        setShowChatGPT( false );
    };

    const askAI = useCallback( async () => {
        setWaiting( true );
        const p = prompt;
        setPrompt( '' );
        
        const formattedTo = to.map( t => {
            return formatRecip( t.toLowerCase() );
        } );

        const sent = await askGPT( user.aid, user.email, prompt, promptType, formattedTo );
        setChatResp( sent );
        setWaiting( false );
    }, [ user.aid, user.email, prompt, promptType, to, formatRecip ] );

    const prepAskAI = async ( prompt, ptype ) => {
        setPromptType( ptype );
        setPrompt( prompt );
        setShowChatGPT( true );
        setReplace( true );
    };

    const handleEnter = async ( e ) => {
        if ( e.key === 'Enter' && !e.shiftKey ) {
            e.preventDefault();
            e.stopPropagation();
            await askAI();        
        }
    };

    const displayTypingEffect = useCallback( () => {
        if ( promptType !== 'subject-line-reco' ) {
            setDisplayText( chatResp );
        } else {
            const matches = chatResp.match( /Var1: "(.*?)"\nVar2: "(.*?)"\nVar3: "(.*)"/ );
            if ( matches ) {
                let resp = <ol>{ matches.map( ( sub, i ) => {
                    if ( i === 0 ) {
                        return null;
                    }
                    return <li>{sub} <Button variant='link' className='ms-2 p-0 tf-copy-button' onClick={ () => insertAiSubject( sub ) }><MdContentCopy /></Button></li>
                } )}</ol>;

                setDisplayText( resp );
            } else {
                setDisplayText( chatResp );
            }
        }
    }, [ chatResp, promptType ] );
      
    const switchSender = ( fromEmail ) => {
        const s = senderList.find( f => f.email === fromEmail );
        if ( s ) {
            setFrom( s.email );
            setSignature( s.signature );
        }
    };

    const onDrop = useCallback( acceptedFiles => {
        for ( let i = 0; i < acceptedFiles.length; i++ ) {
            const f = acceptedFiles[i];
            const reader = new FileReader();
            
            reader.onabort = () => {
                toast.success( `The upload process on ${f.path} has been aborted`, { theme: 'dark' } );
            }
            reader.onerror = () => {
                toast.success( `There's been an error while uploading ${f.path}`, { theme: 'dark' } );
            }
            reader.onload = async () => {
                const b = new Blob( [ reader.result ] );
                const ext = f.name.split( '.' )[1];
                let n = createId( 'file' );
                if ( ext ) {
                    n += '.' + ext;
                }
                
                const name = await storeMedia( obj.aid, user.email, n, b, getMimeTypeFromExtension( ext ) );
                
                let cid;
                let lid;

                if ( objType === 'contact' ) {
                    cid = obj.cid;
                } else if ( objType === 'loan' ) {
                    lid = obj.lid;
                    const c = obj.borrowers?.find( f => f.label === 'Primary' );
                    if ( c ) {
                        cid = c.cid;
                    }
                }

                let file = {
                    mediaUrl: name,
                    name: f.name
                }
                if ( cid ) {
                    file['cid'] = cid;
                }
                if ( lid ) {
                    file['lid'] = lid;
                }

                await createFile( obj.aid, user.email, file );

                setAttachments( prev => [
                    ...prev,
                    { name: file.name, fid: file.mediaUrl }
                ] );
                // toast.success( `Successfully uploaded ${f.name}`, { theme: 'dark' } );
            }
            reader.readAsArrayBuffer( f );
        }
    }, [ user.email, obj, objType ] );

    const { getRootProps, getInputProps, isDragActive } = useDropzone( { onDrop } );

    const removeAttachment = ( file ) => {
        setAttachments( prev => {
            const list = prev.filter( f => f.name !== file );
            return [ ...list ];
        } );
    }

    const renderTemplate = useCallback( ( content, noEscape = false ) => {
        if ( !content || !content.length ) {
            return '';
        }

        const a = accts.find( f => f.id === obj.aid );
        const owner = ( a ) ? team.find( f => f.email === a.owner ) : user;
        
        let params = {
            user: user,
            owner: owner,
            extra: extraFields,
            rates: rates
        };

        if ( objType === 'contact' ) {
            const refs = [];

            if ( obj.priRole === 'partner' ) {
                obj['activeLeads'] = false;
                obj['activePAs'] = false;
                obj['activeLoans'] = false;

                Object.keys( contacts ).forEach( cid => {
                    const c = contacts[cid];
                    if ( c.referredById === obj.cid ) {
                        const los = [];
                        const ls = [];

                        Object.keys( opps ).forEach( o => {
                            const lo = opps[o];
                            if ( lo.relationships && lo.relationships.find( f => f.cid === c.cid ) ) {
                                const q = globalStatus.find( f => f.value === lo.status );

                                if ( [ 'lead', 'talking', 'app-sent', 'setup', 'review' ].includes( q?.queue ) ) {
                                    obj['activeLeads'] = true;
                                    lo['queue'] = 'lead';
                                    los.push( lo );
                                }
                            }
                        } );

                        Object.keys( loans ).forEach( lid => {
                            const l = loans[lid];
                            if ( l.borrowers && l.borrowers.find( f => f.cid === c.cid ) ) {
                                const q = loanStatus.find( f => f.value === l.status );

                                if ( q?.queue === 'pa' ) {
                                    obj['activePAs'] = true;
                                    l['queue'] = 'pa';
                                    ls.push( l );
                                }

                                if ( q?.queue === 'active' ) {
                                    obj['activeLoans'] = true;
                                    l['queue'] = 'active';
                                    ls.push( l );
                                }
                            }
                        } );

                        refs.push( {
                            ...c,
                            opps: los,
                            loans: ls
                        } );
                    }
                } );
            }

            params['contact'] = {
                ...obj,
                referredBy: contacts[obj.referredById],
                referrals: refs
            };
        } else if ( objType === 'loan' ) {
            const pri = obj.borrowers.find( f => f.label === 'Primary' );
            const c = contacts[pri ? pri.cid : obj.borrowers[0].cid];
            const lp2 = user.lists.team.find( f => f.uid === obj.lp2 );
            params['contact'] = {
                ...c,
                referredBy: contacts[c.referredById]
            };
            params['loan'] = {
                ...obj,
                buyersAgent: contacts[obj.baId],
                buyersAgentTC: contacts[obj.baTcId],
                listingAgent: contacts[obj.laId],
                listingAgentTC: contacts[obj.laTcId],
                escrow: contacts[obj.escrowOfficerId],
                title: contacts[obj.titleRepId],
                lp2rep: lp2
            };
        }

        //
        // Need to add way for owner details to be passed in
        //
        // if ( obj.aid && !obj.shared ) {
        //     const owner = user.lists.teams.find( f => )
        // }
        
        return render( content, params, contacts );
    }, [ user, contacts, opps, loans, globalStatus, loanStatus, obj, objType, accts, team, extraFields, rates ] );

    const insertVideo = useCallback( ( l, i ) => {
        const v = `<a href="${l}"><img src="${i}" /></a>`;
        editorRef.current.insertContent( v );
    }, [ editorRef ] );

    const bbCallback = useCallback( ( video ) => {
        setShowVideo( false );
        bbObjRef.current.removeIframe();
        insertVideo( video.link, video.thumbnail );
    }, [ insertVideo ] );

    const bbRef = useCallback( ( r ) => {
        if ( r ) {
            bbObjRef.current.embedIframe( r );
        }
    }, [ bbObjRef ] );

    const handleAddChip = ( chip, label ) => {
        switch ( label ) {
            case 'to':
                setTo( [ ...to, chip ] );
                break;

            case 'cc':
                setCc( [ ...cc, chip ] );
                break;

            case 'bcc':
                setBcc( [ ...bcc, chip ] );
                break;

            default:
                console.log( 'Unknown chip type for addition: ' + label );
        }
    };

    const handleDeleteChip = ( chip, idx, label ) => {
        let newList;
        
        switch ( label ) {
            case 'to':
                newList = to.filter( ( addr, i ) => i !== idx );
                setTo( [ ...newList ] );
                break;

            case 'cc':
                newList = cc.filter( ( addr, i ) => i !== idx );
                setCc( [ ...newList ] );
                break;

            case 'bcc':
                newList = bcc.filter( ( addr, i ) => i !== idx );
                setBcc( [ ...newList ] );
                break;

            default:
                console.log( 'Unknown chip type for delete: ' + label );
        }
    };

    const validateEmail = ( chip ) => {
        const allowed = /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;
        return chip.match( allowed );
    };

    const send = async ( d, t, tz ) => {
        setSending( true );

        const e = editorRef.current.getContent();
        const body = await extractImages( user.aid, user.email, e );
        // const styledBody = e.replace( /<p>/g, '<p class="tf" style="font-size: 12pt; font-family: Calibri, sans-serif;">' );
        const formattedTo = to.map( t => {
            return formatRecip( t.toLowerCase() );
        } );
        const formattedCc = cc.map( t => {
            return formatRecip( t.toLowerCase() );
        } );
        const formattedBcc = bcc.map( t => {
            return formatRecip( t.toLowerCase() );
        } );
        const f = senderList.find( f => f.email === from );
        const formattedFrom = { email: f.email, name: f.name };

        let msg = {
            subject: subject,
            to: formattedTo,
            from: formattedFrom,
            cc: formattedCc,
            bcc: formattedBcc,
            body: body, // e,
            tmpl: tmplUsed,
            attachments: attachments,
            date: d,
            time: t,
            tz: tz,
            channel: 'email'
        };

        if ( objType === 'loan' ) {
            msg['lid'] = obj.lid;
            const c = obj.borrowers?.find( f => f.label === 'Primary' );
            if ( c ) {
                msg['contactId'] = c.cid;
            }
        } else if ( objType === 'contact' ) {
            msg['contactId'] = obj.cid;
        }

        console.log( 'msg: ', msg );
        
        const sent = await sendSingleEmail( obj.aid, user.email, msg );
        // if ( sent.length ) {
            setSending( false );
            toast.success( `The Email has been Sent`, { theme: 'dark' } );
            close();
        // }
        // console.log( sent );
    };

    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '75%',
        bgcolor: 'background.paper',
        border: '1px solid #000',
        p: 2,
        maxHeight: '95vh',
        overflowY: 'auto'
    };

    const loadTemplates = ( editor ) => {
        let list = [];
        
        if ( Object.keys( tmplRef.current ).length ) {
            Object.keys( tmplRef.current ).sort().forEach( name => {
                const t = tmplRef.current[name];
                if ( !t.objects || t.objects.includes( objType ) ) {
                    const content = renderTemplate( t.body );
                    let fresh = false;
                    let sig = '';

                    list.push( {
                        type: 'menuitem',
                        text: t.name,
                        onAction: () => {
                            if ( t.to ) {
                                const rTo = renderTemplate( t.to );
                                const addrs = rTo.split( ',' );
                                setTo( addrs.filter( f => f.length > 4 ) );
                            }
                            if ( t.cc ) {
                                const rCc = renderTemplate( t.cc );
                                const addrs = rCc.split( ',' );
                                setCc( addrs.filter( f => f.length > 4 ) );
                                setShowCc( true );
                            }
                            if ( t.bcc ) {
                                const rBcc = renderTemplate( t.bcc );
                                const addrs = rBcc.split( ',' );
                                setBcc( addrs.filter( f => f.length > 4 ) );
                                setShowBcc( true );
                            }
                            if ( t.from ) {
                                const s = senderRef.current.find( f => f.email === t.from );
                                if ( s ) {
                                    setFrom( t.from );
                                    if ( s.signature && s.signature.length ) {
                                        sig = renderTemplate( s.signature );
                                    } else if ( user.signature && user.signature.length ) {
                                        sig = renderTemplate( user.signature );
                                    }
                                    fresh = true;
                                }
                            }
                            if ( t.subject ) {
                                setSubject( renderTemplate( t.subject ) );
                                // setSubject( t.subject );
                            }
                            if ( t.attachments ) {
                                setAttachments( t.attachments );
                            }

                            if ( fresh ) {
                                if ( t.removeSig === true ) {
                                    editor.setContent( content );
                                } else {
                                    editor.setContent( content + '<br /><br />' + sig );
                                }
                            } else {
                                if ( t.removeSig === true ) {
                                    editor.setContent( content );
                                } else {
                                    editor.insertContent( content );
                                }
                            }
                            
                            setTmplUsed( t.tid );
                        }
                    } );
                }
            } );
        }
        if ( user.signature && user.signature.length ) {
            list.push( {
                type: 'menuitem',
                text: 'Signature',
                onAction: () => editor.insertContent( user.signature )
            } );
        }
        
        return ( list );
    }

    const loadPromptContextSubMenu = ( editor ) => {
        return [
            {
                type: 'item',
                text: 'Summarize',
                onAction: () => {
                    const selectedText = editor.selection.getContent({ format: 'text' });
                    if ( selectedText ) {
                        prepAskAI( selectedText, 'summarize' );
                    }
                },
                onSetup: ( buttonApi ) => {
                    const updateState = () => {
                        const selectedText = editor.selection.getContent( { format: 'text' } );
                        buttonApi.setEnabled( selectedText );
                    };
                    editor.on( 'NodeChange', updateState );
                    return () => {
                        editor.off( 'NodeChange', updateState );
                    };
                },
            },
            {
                type: "item",
                text: 'Explain',
                onAction: () => {
                    const selectedText = editor.selection.getContent({ format: 'text' });
                    if ( selectedText ) {
                        prepAskAI( selectedText, 'explain' );
                    }
                },
                onSetup: ( buttonApi ) => {
                    const updateState = () => {
                        const selectedText = editor.selection.getContent( { format: 'text' } );
                        buttonApi.setEnabled( selectedText );
                    };
                    editor.on( 'NodeChange', updateState );
                    return () => {
                        editor.off( 'NodeChange', updateState );
                    };
                },
            },
            {
                type: 'item',
                text: 'Improve Writing',
                onAction: () => {
                    const selectedText = editor.selection.getContent({ format: 'text' });
                    if ( selectedText ) {
                        prepAskAI( selectedText, 'improve-writing' );
                    }
                },
                onSetup: ( buttonApi ) => {
                    const updateState = () => {
                        const selectedText = editor.selection.getContent( { format: 'text' } );
                        buttonApi.setEnabled( selectedText );
                    };
                    editor.on( 'NodeChange', updateState );
                    return () => {
                        editor.off( 'NodeChange', updateState );
                    };
                },
            },
            {
                type: 'item',
                text: 'Simplify Writing',
                onAction: async () => {
                    const selectedText = editor.selection.getContent({ format: 'text' });
                    if ( selectedText ) {
                        prepAskAI( selectedText, 'simplify-writing' );
                    }
                },
                onSetup: ( buttonApi ) => {
                    const updateState = () => {
                        const selectedText = editor.selection.getContent( { format: 'text' } );
                        buttonApi.setEnabled( selectedText );
                    };
                    editor.on( 'NodeChange', updateState );
                    return () => {
                        editor.off( 'NodeChange', updateState );
                    };
                },
            },
            {
                type: 'item',
                text: 'Overcome Objection',
                onAction: async () => {
                    const selectedText = editor.selection.getContent({ format: 'text' });
                    if ( selectedText ) {
                        prepAskAI( selectedText, 'objection-handler' );
                    }
                },
                onSetup: ( buttonApi ) => {
                    const updateState = () => {
                        const selectedText = editor.selection.getContent( { format: 'text' } );
                        buttonApi.setEnabled( selectedText );
                    };
                    editor.on( 'NodeChange', updateState );
                    return () => {
                        editor.off( 'NodeChange', updateState );
                    };
                },
            },
            {
                type: 'item',
                text: 'Check Guideline Accuracy',
                onAction: () => {
                    const selectedText = editor.selection.getContent({ format: 'text' });
                    if ( selectedText ) {
                        prepAskAI( selectedText, 'guideline-accuracy' );
                    }
                },
                onSetup: ( buttonApi ) => {
                    const updateState = () => {
                        const selectedText = editor.selection.getContent( { format: 'text' } );
                        // buttonApi.setEnabled( selectedText );
                    };
                    buttonApi.setEnabled( false );
                    editor.on( 'NodeChange', updateState );
                    return () => {
                        editor.off( 'NodeChange', updateState );
                    };
                },
            }
        ]
    }

    const loadPromptContextMenu = ( editor ) => {
        return [
            {
                type: 'submenu',
                text: 'MortgageGPT',
                icon: 'ai',
                getSubmenuItems: () => {
                    const selectedText = editor.selection.getContent( { format: 'text' } );
                    return selectedText ? loadPromptContextSubMenu( editor ) : ''; // Show submenu items if text is selected
                },
            }
        ]
    }

    const loadPromptMenu = ( editor ) => {
        return [ 
            {
                type: 'menuitem',
                text: 'Overcome Objections',
                onAction: () => {
                    prepAskAI( editorRef.current.getContent(), 'objection-handler' );
                }
            }, 
            {
                type: 'menuitem',
                text: 'Check Guideline Accuracy',
                enabled: false,
                onAction: () => {
                    prepAskAI( editorRef.current.getContent(), 'guideline-accuracy' );
                }
            },
            {
                type: 'separator'
            },
            {
                type: 'menuitem',
                text: 'Suggest a Subject Line',
                onAction: () => {
                    prepAskAI( editorRef.current.getContent(), 'subject-line-reco' );
                }
            },
            {
                type: 'separator'
            },
            {
                type: 'menuitem',
                text: 'Summarize',
                onAction: () => {
                    prepAskAI( editorRef.current.getContent(), 'summarize' );
                }
            },
            {
                type: 'menuitem',
                text: 'Explain',
                onAction: () => {
                    prepAskAI( editorRef.current.getContent(), 'explain' );
                }
            },
            {
                type: 'menuitem',
                text: 'Improve Writing',
                onAction: () => {
                    prepAskAI( editorRef.current.getContent(), 'improve-writing' );
                }
            },
            {
                type: 'menuitem',
                text: 'Simplify Writing',
                onAction: () => {
                    prepAskAI( editorRef.current.getContent(), 'simplify-writing' );
                }
            }
        ]
    }

    useEffect( () => {
        if ( chatResp.length ) {
            displayTypingEffect();
        }
    }, [ chatResp, displayTypingEffect ] );

    useEffect( () => {
        let temps = {};

        const a = accts.find( f => f.id === obj.aid );
        if ( a && a.crm && a.crm.meta && a.crm.meta.templates && a.crm.meta.templates.length ) {
            a.crm.meta.templates.forEach( t => {
                temps[t.name] = t;
            } );
        }
        if ( user.templates && user.templates.length ) {
            user.templates.forEach( t => {
                temps[t.name] = t;
            } );
        }

        setAllTmpls( temps );
    }, [ user.templates, accts, obj.aid, obj ] );

    useEffect( () => {
        if ( obj?.email?.length && !loaded ) {
            setLoaded( true );
            const tmpTo = [ obj.email ];
            if ( obj.emailPartner1 ) {
                tmpTo.push( obj.emailPartner1 );
            }
            if ( obj.emailPartner2 ) {
                tmpTo.push( obj.emailPartner2 );
            }
            setTo( tmpTo );
        
            if ( obj?.emailCc?.length ) {
                const emails = obj.emailCc.split( /[,;\s]+/ );
                setCc( [ ...cc, ...emails ] );
                setShowCc( true );
            }
            if ( obj?.emailBcc?.length ) {
                const emails = obj.emailBcc.split( /[,;\s]+/ );
                setBcc( [ ...bcc, ...emails ] );
                setShowBcc( true );
            }
        }
    }, [ obj, loaded, cc, bcc ] );

    useEffect( () => {
        if ( tmplContent && tmplContent.length ) {
            setBody( renderTemplate( tmplContent ) );
        } else if ( tmpl ) {
            let t;
            let sig = signature;

            if ( loadedTmpl || !sendersLoaded ) {
                console.log( 'gotta wait...' );
                return;
            }
            
            if ( user.templates && user.templates.length ) {
                t = user.templates.find( f => f.tid === tmpl );
            }

            if ( !t ) {
                const a = accts.find( f => f.id === obj.aid );
                if ( a && a.crm && a.crm.meta.templates && a.crm.meta.templates.length ) {
                    t = a.crm.meta.templates.find( f => f.tid === tmpl );
                }
            }
            
            if ( t ) {
                if ( t.to ) {
                    const rTo = renderTemplate( t.to );
                    const addrs = rTo.split( ',' );
                    setTo( addrs.filter( f => f.length > 4 ) );
                }
                if ( t.cc ) {
                    const rCc = renderTemplate( t.cc );
                    const addrs = rCc.split( ',' );
                    setCc( addrs.filter( f => f.length > 4 ) );
                    setShowCc( addrs.length );
                }
                if ( t.bcc ) {
                    const rBcc = renderTemplate( t.bcc );
                    const addrs = rBcc.split( ',' );
                    setBcc( addrs.filter( f => f.length > 4 ) );
                    setShowBcc( addrs.length );
                }
                if ( t.from ) {
                    const s = senderList.find( f => f.email === t.from );
                    const tm = team.find( f => f.email === t.from );

                    if ( s ) {
                        setFrom( t.from );
                        if ( s.signature && s.signature.length ) {
                            sig = s.signature;
                        } else if ( tm && tm.signature && tm.signature.length ) {
                            sig = tm.signature;
                        } else if ( user.signature && user.signature.length ) {
                            sig = user.signature;
                        }
                    }
                }
                if ( t.attachments ) {
                    setAttachments( t.attachments );
                }
                
                setSubject( renderTemplate( t.subject ) );

                if ( t.removeSig === true ) {
                    setBody( renderTemplate( t.body ) );
                } else {
                    setBody( renderTemplate( t.body ) + renderTemplate( sig ) );
                }

                setSignature( sig );
                
                setTmplUsed( t.tid );
            }

            setLoadedTmpl( true );
        } else if ( signature && signature.length ) {
            setBody( '<br /><br />' + renderTemplate( signature ) );
        }

        if ( attachment ) {
            setAttachments( prev => {
                const list = prev.filter( f => f.name !== attachment.name );
                return [ ...list, { ...attachment } ];
            } );
        }
    }, [ tmplContent, tmpl, signature, renderTemplate, senderList, attachment, user.signature, user.templates, obj.aid, accts, team, loadedTmpl, sendersLoaded ] );

    useEffect( () => {
        if ( user.stack && user.stack.video ) {
            switch ( user.stack.video.type ) {
                case 'BombBomb':
                    if ( window.BbIntegrate ) {
                        const bb = new window.BbIntegrate( 'd4efd0d8-d20b-490c-0fc1-16f0424ba2e9', { onVideo: bbCallback } );
                        bbObjRef.current = bb;
                        // setVideoObj( bb );
                        setVideoOption( true );
                    }
                    break;

                default:
            }
        }
    }, [ user.stack, editorRef, bbCallback ] );

    useEffect( () => {
        if ( user && !sendersLoaded ) {
            setFrom( user.email );
            setSignature( user.signature );

            let list = [ {
                email: user.email,
                name: user.name,
                signature: user.signature
            } ];

            if ( user.additionalEmailSenders && user.additionalEmailSenders.length ) {
                user.additionalEmailSenders.forEach( s => {
                    if ( !s.signature ) {
                        const tm = team.find( f => f.email === s.email );
                        if ( tm && tm.signature ) {
                            s.signature = tm.signature;
                        }
                    }

                    if ( obj.aid === s.aid && s.default !== false ) {
                        console.log( 'Obj aid matches additional email sender aid: ', s );
                        setFrom( s.email );
                        setSignature( s.signature );
                    }
                    list.push( s );
                } );
            }
            
            setSenderList( list );
            setSendersLoaded( true );
        }
    }, [ user, team, sendersLoaded, obj ] );

    useEffect( () => {
        if ( replace && prompt.length ) {
            askAI();
        }
    }, [ replace, prompt, askAI ] );

    return (
        <Fragment>
            <Box sx={style}>
                <Container fluid className=''>
                    <Row className='pt-2 pb-3 align-items-center tf-email-header-row'>
                        <Col xs='auto' className='tf-email-header-name'>From</Col>
                        {
                            ( senderList.length === 1 ) ? (
                                <Col className='tf-email-header-value'>{user.email}</Col>
                            ) : (
                                <Col xs='auto' className='tf-email-header-value tf-form-select-field me-1'>
                                    <Form.Select size='sm' className='tf-modal-select-input' value={from} onChange={ ( e ) => switchSender( e.target.value ) }>
                                        {
                                            senderList.map( ( s, idx ) => <option key={idx} value={s.email}>{s.email}</option> )
                                        }
                                    </Form.Select>
                                </Col>
                            )
                        }
                        
                    </Row>
                    <Row className='py-2 align-items-center tf-email-header-row'>
                        <Col xs='auto' className='tf-email-header-name'>To</Col>
                        <Col>
                            <ChipInput 
                                value={to}
                                onBeforeAdd={ ( chip ) => validateEmail( chip ) }
                                onAdd={ ( chip ) => handleAddChip( chip, 'to' ) } 
                                onDelete={ ( chip, index ) => handleDeleteChip( chip, index, 'to' ) } 
                                disableUnderline={true}
                                newChipKeyCodes={ [ 9, 13, 32, 186, 188 ] } // Tab, Enter, Space, Semicolon, Comma
                            />
                        </Col>
                        <Col xs='auto'>
                            <Button variant='link' className='pe-2' size='sm' onClick={() => setShowCc( !showCc )}>Cc</Button>
                            <Button variant='link' className='ps-2' size='sm' onClick={() => setShowBcc( !showBcc )}>Bcc</Button>
                        </Col>
                    </Row>
                    {
                        showCc ? (    
                            <Row className='py-2 align-items-center tf-email-header-row'>
                                <Col xs='auto' className='tf-email-header-name'>Cc</Col>
                                <Col>
                                    <ChipInput 
                                        value={cc}
                                        onBeforeAdd={ ( chip ) => validateEmail( chip ) }
                                        onAdd={ ( chip ) => handleAddChip( chip, 'cc' ) } 
                                        onDelete={ ( chip, index ) => handleDeleteChip( chip, index, 'cc' ) } 
                                        disableUnderline={true}
                                        newChipKeyCodes={ [ 9, 13, 32, 186, 188 ] } // Tab, Enter, Space, Semicolon, Comma
                                    />
                                </Col>
                            </Row>
                        ) : (
                            <Fragment />
                        )
                    }
                    {
                        showBcc ? (    
                            <Row className='py-2 align-items-center tf-email-header-row'>
                                <Col xs='auto' className='tf-email-header-name'>Bcc</Col>
                                <Col>
                                    <ChipInput 
                                        value={bcc}
                                        onBeforeAdd={ ( chip ) => validateEmail( chip ) }
                                        onAdd={ ( chip ) => handleAddChip( chip, 'bcc' ) } 
                                        onDelete={ ( chip, index ) => handleDeleteChip( chip, index, 'bcc' ) } 
                                        disableUnderline={true}
                                        newChipKeyCodes={ [ 9, 13, 32, 186, 188 ] } // Tab, Enter, Space, Semicolon, Comma
                                    />
                                </Col>
                            </Row>
                        ) : (
                            <Fragment />
                        )
                    }
                    <Row className='py-3 align-items-center'>
                        <Col xs='auto' className='tf-email-header-name'>Subject</Col>
                        <Col className='tf-form-text-field ps-0'><Form.Control value={subject} className='px-0' onChange={ ( e ) => setSubject( e.target.value ) } /></Col>
                    </Row>
                </Container>
                    <Editor
                        tinymceScriptSrc={process.env.PUBLIC_URL + '/tinymce/tinymce.min.js'}
                        init={ { 
                            menubar: false, 
                            statusbar: false,
                            toolbar_mode: 'wrap',
                            visual: false,
                            font_family_formats: 'Calibri="Calibri",sans-serif; Arial=Arial,Helvetica,sans-serif; Helvetica=Helvetica,Arial,sans-serif; Times New Roman=Times New Roman,Times,serif',
                            font_size_formats: '8pt 10pt 11pt 12pt 14pt 16pt 18pt 24pt 36pt 48pt 64pt',
                            font_size_input_default_unit: 'px',
                            content_style: `
                                body { font-family: ${ settings?.editor?.fontFamily ? settings.editor.fontFamily + ', ' : '' }Arial, Helvetica, sans-serif; font-size: ${ settings?.editor?.fontSize ? settings.editor.fontSize : '12pt' }; }
                                p, li, td, div, span { font-family: inherit; font-size: ${ settings?.editor?.fontSize ? settings.editor.fontSize : '12pt' }; }
                            `,
                            setup: ( editor ) => {
                                editor.ui.registry.addButton( 'tfvideo', {
                                    icon: 'embed',
                                    tooltip: 'Insert Video',
                                    enabled: true,
                                    onAction: (_) => {
                                        setShowVideo( true );
                                    },
                                } );
                                editor.ui.registry.addMenuButton( 'tftmpl', {
                                    text: 'Insert Template...',
                                    fetch: ( cb ) => {
                                        const items = loadTemplates( editor );
                                        cb( items );
                                    }
                                } );
                                editor.ui.registry.addMenuButton( 'tfaiprompts', {
                                    icon: 'ai-prompt',
                                    fetch: ( cb ) => {
                                      const items = loadPromptMenu();
                                      cb( items );
                                    }
                                } );
                                editor.ui.registry.addButton( 'tfchatgpt', {
                                    icon: 'ai',
                                    tooltip: 'Ask MortgageGPT',
                                    onAction: () => {
                                        setShowChatGPT( true );
                                    }
                                });
                                
                                editor.ui.registry.addContextMenu( 'aiContextMenu', {
                                    update: ( element ) => {
                                        const selectedText = editor.selection.getContent( { format: 'text' } );
                                        return selectedText ? loadPromptContextMenu( editor ) : ''; // Show the custom item if text is selected
                                    },
                                } );
                            },
                            browser_spellcheck: true,
                            contextmenu: 'aiContextMenu link'
                        } }
                        onInit={ ( evt, editor ) => {
                            editorRef.current = editor;
                        } }
                        initialValue={body}
                        plugins='emoticons code lists link image'
                        toolbar='fontfamily fontsize | bold italic underline strikethrough | forecolor backcolor | align bullist numlist | link | image tfvideo emoticons tftmpl tfchatgpt tfaiprompts | code'
                    />
                <Container fluid className='p-0 pt-2' id='tf-bbv-container' />
                {
                    attachments.length ? (
                        <Container fluid className='pe-0'>
                            <Row className='w-100 py-2 me-0'>
                                { attachments.map( a => <Col key={a.name} xs='auto'><span className='tf-email-attachment-file'>{a.name}</span><span className='tf-email-attachment-delete' onClick={ () => removeAttachment( a.name ) }>X</span></Col>) }
                            </Row>
                        </Container>
                    ) : (
                        null
                    )
                }
                <Container fluid className='pe-0'>
                    <Row className='w-100 py-2 me-0 align-items-center'>
                        <Col className='p-2 text-center' { ...getRootProps() } style={ { color: 'var( --tf-blue )', fontWeight: 600, border: '1px dotted var( --tf-blue )' } }>
                            <input { ...getInputProps() } />
                            <Row><Col>Drop Attachments or <Button variant='outline-primary' size='sm'>Select Files</Button></Col></Row>
                        </Col>
                    </Row>
                </Container>
                <Container fluid>
                    <Row className='w-100 p-2 align-items-center'>
                        <Col className='pe-2 text-end'><ScheduleButton send={send} disabled={!subject.length || sending} /></Col>
                        <Col className='ps-2 text-start'><Button variant='outline-dark' onClick={close}>Cancel</Button></Col>
                    </Row>
                    {
                        ( !subject.length ) ? (
                            <Row className='w-100 p-2 align-items-center text-center'>
                                <Col style={ { color: 'red' } }>* Subject is Required</Col>
                            </Row>
                        ) : (
                            null
                        )
                    }
                </Container>
                <Modal open={showVideo} onClose={ () => setShowVideo( false ) }>
                    <Box sx={ { ...style, width: '835px' } } id='tf-bbv-container' ref={bbRef} />
                </Modal>
                <Modal open={showChatGPT} onClose={ () => setShowChatGPT( false ) }>
                    <Box 
                        id='tf-chatgpt-container'
                        sx={ {
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '50%',
                            height: 500,
                            bgcolor: 'background.paper',
                            border: '2px solid #000',
                            boxShadow: 24,
                            p: 2,
                            display: 'flex',
                            flexDirection: 'column',
                        } }
                    >
                        <Button
                            variant="outline"
                            onClick={ () => setShowChatGPT( false ) }
                            style={ { position: 'absolute', top: '5px', right: '5px' } }
                        >
                            X
                        </Button>
                        <Typography variant="h5" id="modal-title">
                            AI Assistant
                        </Typography>
                        {
                            ( waiting ) ? (
                                <div style={ { height: '100%', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' } }>
                                    <Spinner animation='border' />
                                </div>
                            ) : (
                                <Box sx={{ overflowY: 'auto', flex: 1, paddingTop: '20px' }}>
                                    {
                                        ( promptType !== 'subject-line-reco' ) ? (
                                            <div dangerouslySetInnerHTML={ { __html: displayText } } />
                                        ) : (
                                            displayText
                                        )
                                    }
                                </Box>
                            )
                        }
                        { chatResp && (
                            <Row className='w-100 p-2 align-items-center'>
                                <Col className='pe-2 text-end'><Button variant='outline-dark' color='primary' onClick={insertAiResponse}>Insert</Button></Col>
                                <Col className='ps-2 text-start'><Button variant='outline-dark' color='primary' onClick={ () => alert( 'try again' ) }>Try Again</Button></Col>
                            </Row>
                        ) }
                        <TextField 
                            id="chat-prompt" 
                            label="Send a message" 
                            multiline 
                            minRows={1} 
                            maxRows={5} 
                            value={prompt}
                            onChange={ ( e ) => { setPrompt( e.target.value ) } }
                            onKeyDown={ ( e ) => handleEnter( e ) }
                            variant="outlined" 
                            sx={ {
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '10px',
                                    boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)',
                                    padding: '16.5px 14px'
                                },
                            } }
                            InputProps={ {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton edge="end" onClick={ () => askAI() }>
                                            <SendIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            } }
                            fullWidth
                        />
                    </Box>
                </Modal>
            </Box>
        </Fragment>
    )
}

const mapProps = ( state ) => ( {
    user: state.user,
    settings: state.user.meta.settings,
    accts: state.user.lists.accounts,
    team: state.user.lists.team,
    contacts: state.contacts.byId,
    contactEmails: state.contacts.byEmail,
    opps: state.leadopps.byId,
    loans: state.loans.byId,
    globalStatus: state.user.meta.global.picklist.status,
    loanStatus: state.user.meta.loan.picklist.status,
    rates: state.user.rates,
} );
  
const mapDispatch = ( dispatch ) => ( {
} );
  
export default connect( mapProps, mapDispatch )( SendEmail );